import { IntlShape } from 'react-intl';

import type { TAuthError } from '@/redux/modules/auth';

import { ENameRejection } from './nameValidation';

/**
 * The approved copy for the two name-rejection causes (MP-6864), defined once.
 *
 * Takes `IntlShape` rather than calling `useIntl` so the same definition serves
 * both the react-hook-form rule builders in `useInputRules.ts` and the non-hook
 * server-error mappers — same shape as `components/auth/getAuthErrorMessage.ts`.
 *
 * Naming the document rather than the alphabet keeps the non-Latin message a
 * document-matching request: every passport carries a romanized name under ICAO
 * 9303, so this asks for something the guest already has.
 */
export const getNameErrorMessage = (intl: IntlShape, rejection: ENameRejection): string | null => {
  switch (rejection) {
    case ENameRejection.NotLatinScript:
      return intl.formatMessage({
        defaultMessage:
          "Enter your name in Latin letters, as it appears on your passport or driver's license.",
        id: '2BKXZI',
        description: 'Name field error > name is not written in Latin script',
      });
    case ENameRejection.InvalidCharacters:
      // Also covers the backend's excluded-term check (wheelbase/outdoorsy/
      // notification/support). That sub-case names a rule the guest didn't break,
      // but it is what the API returns today — the client is not inventing copy,
      // it is saving a round trip. Tracked for a third approved string.
      return intl.formatMessage({
        defaultMessage:
          'Names can only contain letters, spaces, hyphens, apostrophes, and periods.',
        id: 'Bthlja',
        description: 'Name field error > name contains disallowed characters',
      });
    case ENameRejection.LockedByVerification:
      return intl.formatMessage({
        defaultMessage:
          "This name has been verified with a government ID and can't be changed. Contact support for help.",
        id: 'OBspJN',
        description: 'Name field error > name is locked after ID verification',
      });
    case ENameRejection.Ok:
    default:
      return null;
  }
};

/** The name-rejection codes, keyed by the `code` string the API sends. */
const NAME_REJECTION_BY_CODE: Record<string, ENameRejection> = {
  [ENameRejection.NotLatinScript]: ENameRejection.NotLatinScript,
  [ENameRejection.InvalidCharacters]: ENameRejection.InvalidCharacters,
  [ENameRejection.LockedByVerification]: ENameRejection.LockedByVerification,
};

/** react-hook-form field names used by the first/last name forms. */
const FIELD_BY_API_FIELD: Record<string, 'firstName' | 'lastName'> = {
  first_name: 'firstName',
  last_name: 'lastName',
  name: 'firstName',
};

/**
 * Turn a failed user-update response into an inline field error, when it was a
 * name rejection. Returns null for anything else, so callers keep their existing
 * generic-toast path for genuine failures.
 *
 * Reads the **top-level** `code` for the cause and `errors[0].field` for the
 * input — serrors does not put a code on nested validation entries, so a caller
 * switching on something inside `errors[]` would find nothing there.
 *
 * Shared by profile edit and both checkout protection dialogs; they all render
 * the same two name inputs against the same API rule.
 */
export const getNameFieldError = (
  intl: IntlShape,
  error?: TAuthError | null,
): { field: 'firstName' | 'lastName'; message: string } | null => {
  if (!error || typeof error === 'string') return null;

  const rejection = NAME_REJECTION_BY_CODE[String(error.code)];
  if (!rejection) return null;

  const message = getNameErrorMessage(intl, rejection);
  if (!message) return null;

  // Default to the first name when the server didn't name a field — showing the
  // message somewhere beats swallowing it into a generic toast.
  const field = FIELD_BY_API_FIELD[error.errors?.[0]?.field ?? ''] ?? 'firstName';
  return { field, message };
};

/** Same as `getNameFieldError`, for the one form that also has a Preferred name input. */
export const getProfileNameFieldError = (
  intl: IntlShape,
  error?: TAuthError | null,
): { field: 'firstName' | 'lastName' | 'preferredName'; message: string } | null => {
  const nameFieldError = getNameFieldError(intl, error);
  if (!nameFieldError) return null;

  const apiField = typeof error === 'object' ? error?.errors?.[0]?.field : undefined;
  return apiField === 'preferred_name'
    ? { field: 'preferredName', message: nameFieldError.message }
    : nameFieldError;
};
